import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useContext, useState } from "react";

import Button from "@components/Button";
import FloatingLabelInput from "@components/FloatingLabelInput";
import Input from "@components/Input";
import Typography from "@components/Typography";
import colors from "@variables/colors";
import fontSize from "@variables/font-size";
import fontStyle from "@variables/font-style";
import layout from "@variables/layout";
import margin from "@variables/margins";
import padding from "@variables/paddings";
import Globals from "src/assets/styles/globals";
import ApiContext from "src/lib/contexts/ApiContext";
import { resetPassword } from "src/lib/ValidationSchema/createPasswordSchema";
import handleValidationError, {
  resetValidatonErrors,
} from "src/utils/handleValidationError";

import { appToast } from "./_app";

const ChangePasswordPage = () => {
  const { apiClient, setUser } = useContext(ApiContext);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({
    email: "",
  });
  const [emailSend, setEmailSend] = useState(false);

  const handleSubmitForm = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    try {
      await resetPassword.validate(
        { email },
        {
          abortEarly: false,
        }
      );
    } catch (err) {
      setErrors(handleValidationError(err));
      setLoading(false);
      return;
    }

    try {
      const res = await apiClient.resetPassword({
        email,
      });
      setEmailSend(true);
    } catch (err: any) {
      resetValidatonErrors(errors, setErrors);
      appToast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Head>
        <title>Change password</title>
      </Head>
      <Globals />
      <Logo>
        <Image
          src={"/LOGO WITHOUT TYPE.png"}
          alt="dendots"
          width={"144"}
          height={"144"}
          objectFit="contain"
        />
      </Logo>
      {emailSend ? (
        <Message>
          <h1>PLEASE CHECK YOU EMAIL TO RESET YOUR PASSWORD</h1>
          <Button theme="primary" css={loginButtonCss}>
            <a
              css={css`
                width: 100%;
                height: 100%;
                display: block;
              `}
              href="login"
            >
              BacK
            </a>
          </Button>
        </Message>
      ) : (
        <LoginForm>
          <LoginTitle as="h1" content="CHANGE PASSWORD" />
          <div
            css={css`
              display: flex;
              align-items: flex-end;
              flex-direction: column;
              gap: 0.8em;
              & > * {
                width: 100%;
              }
            `}
          >
            <FloatingLabelInput
              withborder={true}
              label="Email"
              onChange={(e: any) => setEmail(e.target.value)}
              value={email ?? ""}
              error={errors.email ?? ""}
              type={"email"}
            />
          </div>
          <Button
            label="Next"
            theme="primary"
            css={loginButtonCss}
            onClick={handleSubmitForm}
          />
          <SignUpText>
            Do not you have an account?
            <Link href={"/login"}>Login</Link>
          </SignUpText>
        </LoginForm>
      )}
    </Wrapper>
  );
};

export default ChangePasswordPage;

const Wrapper = styled.div`
  padding: 2em;
  min-height: 100vh;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const LoginTitle = styled(Typography)`
  text-align: center;
  font-family: ${fontStyle.family.montserrat};
  font-size: ${fontSize[7]};
  font-weight: ${fontStyle.weight.bold};
  padding-bottom: ${padding[6]};
  color: ${colors.gray[800]};
`;

const LoginForm = styled.form`
  border: 1px solid #cecece;
  border-radius: 20px;
  padding: 3em;
  width: 460px;
  max-width: 80%;
  ${layout.mediaBreakpointDown("lg")} {
    max-width: 100%;
    padding: 1.5em;
  }
`;

const loginButtonCss = css`
  width: 100%;
  margin-top: 2.5em;
`;

const SignUpText = styled.div`
  font-family: ${fontStyle.family["montserrat"]};
  font-size: ${fontSize[1]};
  color: ${colors.gray[400]};
  padding-top: ${padding[9]};
  a {
    color: ${colors.blue[500]};
    padding: 0 0.5em;
  }
`;

const Logo = styled.div`
  width: fit-content;
  margin: 0 auto ${margin[10]};
  ${layout.mediaBreakpointDown("lg")} {
    width: 35%;
  }
`;

const Message = styled.div`
  border: 1px solid #cecece;
  border-radius: 20px;
  padding: 3em;
  width: 460px;
  max-width: 80%;
  text-align: center;
  ${layout.mediaBreakpointDown("lg")} {
    max-width: 100%;
    padding: 1.5em;
  }
`;
